import Accordion from "@/components/Accordion";
import Slider from "@/components/Slider";
import ColorPicker from "@/components/ColorPicker";
import { StyleConfigModel } from "@/pages/Messaging/types";
import { Toggle } from "@/components/Toggle";
import React from "react";
import { ChatWindow } from "../../types";
import LogoUpload from "../../LogoUpload";

interface Props {
  onExpanded(): void;
  workingChatWindow: ChatWindow;
  setWorkingChatWindow: (chatWindow: ChatWindow) => void;
  setStagedLauncherIconFile?: (file: File | null) => void;
}

type LauncherPosition = "Left" | "Right";

const positions: LauncherPosition[] = ["Left", "Right"];

const shadowOptions = [
  { value: "none", label: "None" },
  { value: "sm", label: "Small" },
  { value: "md", label: "Medium" },
  { value: "lg", label: "Large" },
  { value: "xl", label: "Extra Large" }
];

const launcherSizes = [
  { value: "sm", label: "Small" },
  { value: "md", label: "Medium" },
  { value: "lg", label: "Large" }
];

export function Layout(props: Props) {
  const workingStyleConfig = props.workingChatWindow.styleConfig || {};

  const updateStyleConfig = (updates: Partial<StyleConfigModel>) => {
    props.setWorkingChatWindow({
      ...props.workingChatWindow,
      styleConfig: {
        ...workingStyleConfig,
        ...updates
      }
    });
  };

  function clampedNumber(
    e: React.ChangeEvent<HTMLInputElement>,
    min: number,
    max: number,
    fallback: number
  ) {
    let value = parseInt(e.target.value);
    if (isNaN(value)) value = fallback;
    if (value > max) value = max;
    if (value < min) value = min;
    return value;
  }

  return (
    <Accordion.Item eventKey="layout" onExpanded={() => props.onExpanded()}>
      <Accordion.Header>
        <Accordion.Title>Layout</Accordion.Title>
        <Accordion.SubTitle>
          Position, size, border, and shadow of your chat window
        </Accordion.SubTitle>
      </Accordion.Header>
      <Accordion.Body>
        <div className="space-y-5">
          {/* Window Position */}
          <div>
            <div className="text-sm font-medium mb-1">Window Position</div>
            <div className="text-xs text-slate-400 mb-2">Which corner of the webpage the chat launcher button appears in</div>
            <div className="flex gap-4">
              {positions.map((position) => {
                const isSelected =
                  workingStyleConfig.launcherPosition === position;
                return (
                  <div
                    key={position}
                    className={`flex justify-center items-center rounded-lg py-2 px-6 w-36 cursor-pointer border-2 group relative ${
                      isSelected
                        ? "border-primary bg-primary-bg"
                        : "border-slate-300 bg-white hover:border-slate-400"
                    }`}
                    onClick={() =>
                      updateStyleConfig({ launcherPosition: position })
                    }
                    title={`Chat button appears in the bottom-${position.toLowerCase()} corner of your webpage`}
                  >
                    <div className="w-4 h-4 relative bg-slate-500 rounded-sm overflow-hidden flex-shrink-0">
                      <div
                        className={`w-[4px] h-[6px] top-[6px] absolute bg-white ${
                          position === "Left" ? "left-[2px]" : "right-[2px]"
                        }`}
                      />
                    </div>
                    <div className="text-sm font-medium text-slate-600 ml-2">
                      {position}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Horizontal Offset */}
          <div>
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium mb-2">Horizontal Offset</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.sideSpacing ?? 20}
                  onChange={(e) =>
                    updateStyleConfig({
                      sideSpacing: clampedNumber(e, 0, 200, 0)
                    })
                  }
                  min={0}
                  max={200}
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            <Slider
              max={200}
              min={0}
              onChange={(value) => updateStyleConfig({ sideSpacing: value })}
              value={workingStyleConfig.sideSpacing ?? 20}
            />
          </div>

          {/* Vertical Offset */}
          <div>
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium mb-2">Vertical Offset</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.bottomSpacing ?? 20}
                  onChange={(e) =>
                    updateStyleConfig({
                      bottomSpacing: clampedNumber(e, 0, 200, 0)
                    })
                  }
                  min={0}
                  max={200}
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            <Slider
              max={200}
              min={0}
              onChange={(value) => updateStyleConfig({ bottomSpacing: value })}
              value={workingStyleConfig.bottomSpacing ?? 20}
            />
          </div>

          <div className="border-t border-slate-200" />

          {/* Window Width */}
          <div>
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium mb-2">Window Width</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.windowWidth ?? 370}
                  onChange={(e) =>
                    updateStyleConfig({
                      windowWidth: clampedNumber(e, 320, 500, 370)
                    })
                  }
                  min={320}
                  max={500}
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            <Slider
              max={500}
              min={320}
              onChange={(value) => updateStyleConfig({ windowWidth: value })}
              value={workingStyleConfig.windowWidth ?? 370}
            />
          </div>

          {/* Window Height */}
          <div>
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium mb-2">Window Height</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.windowHeight ?? 0}
                  onChange={(e) =>
                    updateStyleConfig({
                      windowHeight: clampedNumber(e, 0, 800, 0)
                    })
                  }
                  min={0}
                  max={800}
                  placeholder="Auto"
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            <Slider
              max={800}
              min={0}
              onChange={(value) => updateStyleConfig({ windowHeight: value })}
              value={workingStyleConfig.windowHeight ?? 0}
            />
            <div className="text-xs text-slate-400 mt-1">Set to 0 for auto height</div>
          </div>

          {/* Border Radius */}
          <div>
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium mb-2">Corner Radius</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.windowBorderRadius ?? 32}
                  onChange={(e) =>
                    updateStyleConfig({
                      windowBorderRadius: clampedNumber(e, 0, 32, 32)
                    })
                  }
                  min={0}
                  max={32}
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            <Slider
              max={32}
              min={0}
              onChange={(value) =>
                updateStyleConfig({ windowBorderRadius: value })
              }
              value={workingStyleConfig.windowBorderRadius ?? 32}
            />
          </div>

          <div className="border-t border-slate-200" />

          {/* Border */}
          <div>
            <div className="text-sm font-medium mb-2">Border</div>
            <div className="flex items-center gap-4 mb-3">
              <div className="text-sm font-normal w-20">Width</div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                  value={workingStyleConfig.windowBorderWidth ?? 0}
                  onChange={(e) =>
                    updateStyleConfig({
                      windowBorderWidth: clampedNumber(e, 0, 5, 0)
                    })
                  }
                  min={0}
                  max={5}
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            {(workingStyleConfig.windowBorderWidth ?? 0) > 0 && (
              <div className="flex items-center gap-4">
                <div className="text-sm font-normal w-20">Color</div>
                <ColorPicker
                  value={workingStyleConfig.windowBorderColor ?? "#e2e8f0"}
                  onChange={(value) =>
                    updateStyleConfig({ windowBorderColor: value })
                  }
                />
              </div>
            )}
          </div>

          {/* Shadow */}
          <div>
            <div className="text-sm font-medium mb-2">Shadow</div>
            <div className="flex flex-wrap gap-2">
              {shadowOptions.map((opt) => {
                const isSelected =
                  (workingStyleConfig.windowShadow ?? "xl") === opt.value;
                return (
                  <div
                    key={opt.value}
                    className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm ${
                      isSelected
                        ? "border-primary bg-primary-bg text-primary-active"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() =>
                      updateStyleConfig({ windowShadow: opt.value })
                    }
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border-t border-slate-200" />

          {/* Launcher Size */}
          <div>
            <div className="text-sm font-medium mb-2">Launcher Size</div>
            <div className="flex gap-3">
              {launcherSizes.map((size) => {
                const isSelected =
                  (workingStyleConfig.launcherSize ?? "md") === size.value;
                return (
                  <div
                    key={size.value}
                    className={`flex items-center justify-center rounded-lg py-2 px-5 cursor-pointer border-2 text-sm ${
                      isSelected
                        ? "border-primary bg-primary-bg text-primary-active"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() =>
                      updateStyleConfig({ launcherSize: size.value })
                    }
                  >
                    {size.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Launcher Appearance */}
          <div className="space-y-3">
            <div className="text-sm font-medium">Launcher Appearance</div>

            {/* State-based colors */}
            {[
              {
                label: "Available",
                bgField: "launcherAvailableBgColor" as keyof typeof workingStyleConfig,
                textField: "launcherAvailableTextColor" as keyof typeof workingStyleConfig,
                defaultBg: workingStyleConfig.launcherBgColor ?? "#1670E7",
                defaultText: workingStyleConfig.launcherTextColor ?? "#FFFFFF",
              },
              {
                label: "Unavailable / Offline",
                bgField: "launcherUnavailableBgColor" as keyof typeof workingStyleConfig,
                textField: "launcherUnavailableTextColor" as keyof typeof workingStyleConfig,
                defaultBg: "#9CA3AF",
                defaultText: "#FFFFFF",
              },
              {
                label: "Chatting",
                bgField: "launcherChattingBgColor" as keyof typeof workingStyleConfig,
                textField: "launcherChattingTextColor" as keyof typeof workingStyleConfig,
                defaultBg: workingStyleConfig.launcherBgColor ?? "#1670E7",
                defaultText: workingStyleConfig.launcherTextColor ?? "#FFFFFF",
              },
            ].map((state) => (
              <div key={state.label}>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">{state.label}</div>
                <div className="grid grid-cols-3 items-center gap-4 mb-2">
                  <div className="text-sm font-normal">Background</div>
                  <ColorPicker
                    value={(workingStyleConfig[state.bgField] as string) ?? state.defaultBg}
                    onChange={(value) => updateStyleConfig({ [state.bgField]: value })}
                  />
                  <div />
                </div>
                <div className="grid grid-cols-3 items-center gap-4">
                  <div className="text-sm font-normal">Icon Color</div>
                  <ColorPicker
                    value={(workingStyleConfig[state.textField] as string) ?? state.defaultText}
                    onChange={(value) => updateStyleConfig({ [state.textField]: value })}
                  />
                  <div />
                </div>
              </div>
            ))}

            <div>
              <div className="text-sm font-normal mb-1">Launcher Label</div>
              <div className="text-xs text-slate-400 mb-1">Optional text shown next to the chat button, e.g. "Chat with us"</div>
              <input
                type="text"
                aria-label="Launcher Label"
                className="border border-slate-300 rounded-md p-2 w-full text-sm"
                placeholder="Optional text next to launcher icon"
                value={workingStyleConfig.launcherText ?? ""}
                onChange={(e) => updateStyleConfig({ launcherText: e.target.value })}
              />
            </div>
            {props.setStagedLauncherIconFile && (
              <LogoUpload
                setStageLogoFile={props.setStagedLauncherIconFile}
                update={updateStyleConfig}
                workingStyleConfig={workingStyleConfig}
                field="launcherIconUrl"
                label="Launcher Icon"
                previewBackground={workingStyleConfig.actionColor}
                sizeGuidance="PNG or SVG • Square image recommended • Replaces the default chat bubble icon"
              />
            )}
          </div>

          <div className="border-t border-slate-200" />

          {/* Open animation */}
          <div>
            <div className="text-sm font-medium mb-2">Open Animation</div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "slideUp", label: "Slide up" },
                { value: "fade", label: "Fade in" },
                { value: "pop", label: "Pop" },
                { value: "none", label: "None" },
              ].map((opt) => {
                const isSelected = (workingStyleConfig.openAnimation ?? "slideUp") === opt.value;
                return (
                  <div
                    key={opt.value}
                    className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm ${
                      isSelected
                        ? "border-primary bg-primary-bg text-primary-active"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() => updateStyleConfig({ openAnimation: opt.value })}
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Page backdrop */}
          <div>
            <div className="text-sm font-medium mb-1">Page Backdrop</div>
            <div className="text-xs text-slate-400 mb-2">Effect applied to the page behind the chat window when it is open</div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "none", label: "None" },
                { value: "dim", label: "Dim" },
                { value: "blur", label: "Blur" },
              ].map((opt) => {
                const isSelected = (workingStyleConfig.chatBackdrop ?? "none") === opt.value;
                return (
                  <div
                    key={opt.value}
                    className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm ${
                      isSelected
                        ? "border-primary bg-primary-bg text-primary-active"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() => updateStyleConfig({ chatBackdrop: opt.value })}
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Scroll to bottom */}
          <div>
            <div className="text-sm font-medium mb-1">Scroll-to-Bottom Indicator</div>
            <div className="text-xs text-slate-400 mb-2">Button or badge shown when the visitor has scrolled up in the conversation — click it to jump back to the latest message</div>
            <div className="flex flex-wrap gap-2">
              {[
                { value: "none", label: "None" },
                { value: "arrow", label: "Arrow button" },
                { value: "badge", label: "New messages badge" },
              ].map((opt) => {
                const isSelected = (workingStyleConfig.scrollToBottomStyle ?? "arrow") === opt.value;
                return (
                  <div
                    key={opt.value}
                    className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm ${
                      isSelected
                        ? "border-primary bg-primary-bg text-primary-active"
                        : "border-slate-300 bg-white text-slate-600"
                    }`}
                    onClick={() => updateStyleConfig({ scrollToBottomStyle: opt.value })}
                  >
                    {opt.label}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border-t border-slate-200" />

          {/* Input & send button */}
          <div className="space-y-4">
            <div>
              <div className="text-sm font-medium mb-1">Input Field Style</div>
              <div className="text-xs text-slate-400 mb-2">Controls how the visitor's message text box looks at the bottom of the chat window</div>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: "box", label: "Box" },
                  { value: "underline", label: "Underline" },
                  { value: "pill", label: "Pill" },
                ].map((opt) => {
                  const isSelected = (workingStyleConfig.inputFieldStyle ?? "box") === opt.value;
                  return (
                    <div
                      key={opt.value}
                      className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm ${
                        isSelected
                          ? "border-primary bg-primary-bg text-primary-active"
                          : "border-slate-300 bg-white text-slate-600"
                      }`}
                      onClick={() => updateStyleConfig({ inputFieldStyle: opt.value })}
                    >
                      {opt.label}
                    </div>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium mb-2">Send Button</div>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: "icon", label: "Icon only" },
                  { value: "text", label: "Text only" },
                  { value: "both", label: "Icon + Text" },
                ].map((opt) => {
                  const isSelected = (workingStyleConfig.sendButtonStyle ?? "icon") === opt.value;
                  return (
                    <div
                      key={opt.value}
                      className={`rounded-lg py-1.5 px-4 cursor-pointer border-2 text-sm ${
                        isSelected
                          ? "border-primary bg-primary-bg text-primary-active"
                          : "border-slate-300 bg-white text-slate-600"
                      }`}
                      onClick={() => updateStyleConfig({ sendButtonStyle: opt.value })}
                    >
                      {opt.label}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200" />

          {/* Behavior toggles */}
          <div>
            <div className="text-sm font-medium mb-3">Behavior</div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm font-normal">Show unread message count</div>
                  <div className="text-xs text-slate-400">Badge on launcher when messages are unread</div>
                </div>
                <Toggle
                  toggled={workingStyleConfig.showUnreadCount ?? true}
                  changeToggle={() =>
                    updateStyleConfig({ showUnreadCount: !(workingStyleConfig.showUnreadCount ?? true) })
                  }
                />
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm font-normal">Start minimized on desktop</div>
                  <div className="text-xs text-slate-400">When ON: visitors see only the launcher button when the page loads — they must click it to open chat. When OFF: chat opens automatically.</div>
                </div>
                <Toggle
                  toggled={workingStyleConfig.forceMinimizedDesktop ?? false}
                  changeToggle={() =>
                    updateStyleConfig({ forceMinimizedDesktop: !workingStyleConfig.forceMinimizedDesktop })
                  }
                />
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm font-normal">Start minimized on mobile</div>
                  <div className="text-xs text-slate-400">Same as above but for mobile devices</div>
                </div>
                <Toggle
                  toggled={workingStyleConfig.forceMinimizedMobile ?? false}
                  changeToggle={() =>
                    updateStyleConfig({ forceMinimizedMobile: !workingStyleConfig.forceMinimizedMobile })
                  }
                />
              </div>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm font-normal">Fullscreen on mobile</div>
                  <div className="text-xs text-slate-400">Expands to fill the entire screen on mobile devices</div>
                </div>
                <Toggle
                  toggled={workingStyleConfig.fullscreenMobile ?? false}
                  changeToggle={() =>
                    updateStyleConfig({ fullscreenMobile: !workingStyleConfig.fullscreenMobile })
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
}
